import React from "react";
import PropTypes from "prop-types";
import { TimerDisplay } from "../TimerDisplay";
import { ButtonControls } from "../ButtonControls";
import { PowerSupplyContext } from "../../../../..";
import { StartStopButton } from "../StartStopButton";
import { useTranslation } from "react-i18next";

import "./style.scss";

const HOUR_RATE = 1;
const MINUTE_RATE = 10;
const LOWERBOUND_TIME = new Date(0, 0, 0).getTime();

const TRANSLATION_KEY = {
  HH: "HH.string",
  MM: "MM.string",
  Timer: "Timer.string",
  IncreaseHourButton: "IncreaseHourButton.string",
  DecreaseHourButton: "DecreaseHourButton.string",
  IncreaseMinutesButton: "IncreaseMinutesButton.string",
  DecreaseMinutesButton: "DecreaseMinutesButton.string",
};

export function TimerBlock(props) {
  const {
    t,
    //i18n
  } = useTranslation();
  const HH = t(TRANSLATION_KEY.HH);
  const MM = t(TRANSLATION_KEY.MM);

  const { power, running, powerSupplyPluggedIn } =
    React.useContext(PowerSupplyContext);

  const incrementHours = () => {
    const time = new Date(props.value.getTime());
    time.setHours(time.getHours() + HOUR_RATE);
    props.setTimer(time);
  };
  const decrementHours = () => {
    if (props.value.getTime() === LOWERBOUND_TIME)
      props.setTimer(new Date(LOWERBOUND_TIME));
    else {
      const time = new Date(props.value.getTime());
      time.setHours(time.getHours() - HOUR_RATE);
      props.setTimer(time);
    }
  };

  const incrementMinutes = () => {
    const time = new Date(props.value.getTime());
    time.setMinutes(time.getMinutes() + MINUTE_RATE);
    props.setTimer(time);
  };
  const decrementMinutes = () => {
    if (props.value.getTime() === LOWERBOUND_TIME)
      props.setTimer(new Date(LOWERBOUND_TIME));
    else {
      const time = new Date(props.value.getTime());
      time.setMinutes(time.getMinutes() - MINUTE_RATE);
      props.setTimer(time);
    }
  };

  const throttleCallback = () => {
    if (props.value.getTime() === LOWERBOUND_TIME) props.onStopCentrifuge();
    else {
      const time = new Date(props.value.getTime());
      time.setSeconds(time.getSeconds() - 1);
      props.setTimer(time);
    }
  };

  React.useEffect(() => {
    if (running) {
      setTimeout(() => {
        throttleCallback();
      }, props.timeInterval);
    }
  }, [running, props.value]);

  const startStopActive =
    power && powerSupplyPluggedIn && props.value.getTime() != LOWERBOUND_TIME;

  const onClickStart = () => {
    if (
      !Object.hasOwn(props.interactionProps, "gelBoxes") ||
      (props.interactionProps.gelBoxes[0].anodeConnected &&
        props.interactionProps.gelBoxes[0].cathodeConnected &&
        !props.interactionProps.gelBoxes[0].open)
    ) {
      startStopActive && props.onClickStart();
    } else {
      if (props.interactionProps.gelBoxes[0].open) {
        props.alert("GEL_BOX_OPEN");
      } else if (
        !props.interactionProps.gelBoxes[0].anodeConnected ||
        !props.interactionProps.gelBoxes[0].cathodeConnected
      ) {
        props.alert("GEL_BOX_DISCONNECTED");
      }
    }
  };

  const onClickStop = () => {
    power && props.onClickStop();
  };

  return (
    <div className={`ps_settings_timer ${props.className} `}>
      <h5 className="ps_settings_timer_title">{t(TRANSLATION_KEY.Timer)}</h5>
      <TimerDisplay timeData={props.value} setTimer={props.setTimer} />
      <div className="ps_settings_timer_controlBlock">
        <ButtonControls
          text={HH}
          increment={incrementHours}
          decrement={decrementHours}
          altText1={t(TRANSLATION_KEY.IncreaseHourButton, {
            hours: props.hours,
            minutes: props.minutes,
            seconds: props.seconds,
          })}
          altText2={t(TRANSLATION_KEY.DecreaseHourButton, {
            hours: props.hours,
            minutes: props.minutes,
            seconds: props.seconds,
          })}
        />
        <ButtonControls
          text={MM}
          increment={incrementMinutes}
          decrement={decrementMinutes}
          altText1={t(TRANSLATION_KEY.IncreaseMinutesButton, {
            hours: props.hours,
            minutes: props.minutes,
            seconds: props.seconds,
          })}
          altText2={t(TRANSLATION_KEY.IncreaseMinutesButton, {
            hours: props.hours,
            minutes: props.minutes,
            seconds: props.seconds,
          })}
        />
      </div>
      <StartStopButton
        onClickStart={onClickStart}
        onClickStop={onClickStop}
        disabled={!startStopActive}
      />
    </div>
  );
}

TimerBlock.propTypes = {
  /**
   * timer value to be displayed
   */
  value: PropTypes.instanceOf(Date),
  /**
   * callback function to set timer value
   */
  setTimer: PropTypes.func,
  /**
   * callback function to start centrifuge
   */
  onRunCentrifuge: PropTypes.func,
  /**
   * callback function stop centrifuge
   */
  onStopCentrifuge: PropTypes.func,
};

TimerBlock.defaultProps = {
  title: "Timer",
  timeInterval: 500,
};
